import * as React from 'react';
import { useState } from 'react';
import {
    useListContext,
    useNotify,
    useRefresh,
    useUnselectAll,
    RaRecord,
} from 'react-admin';
import SelectVerificationDialog from './SelectVerificationDialog';
import { Button } from '@mui/material';
import { dataProvider } from '../../../DataProvider';
import { CheckCircleOutline, BlockOutlined } from '@mui/icons-material';

/**
 * Props for the VerificationActions component
 */
interface VerificationActionsProps {
    /** Whether to display the component */
    display: boolean;
    /** Function to reload the page/data */
    reload?: () => void;
    /** The resource name for API calls (e.g., 'RTP_Categories', 'RTP_Performers') */
    resource?: string;
    /** Action type for API calls */
    action?: string;
    /** The entity type for display text (e.g., 'Categories', 'Performers') */
    entityType: string;
}

/**
 * Extended record type with verification and status fields
 */
interface VerificationRecord extends RaRecord {
    verification_status: number;
    status: number;
}

/**
 * Shared Verification Actions Component
 * Provides bulk action buttons for updating verification status to Whitelist or Blacklist
 * Follows the same pattern as SmEventCheck tag-actions component
 */
const VerificationActions: React.FC<VerificationActionsProps> = ({
    display,
    reload,
    resource,
    action,
    entityType
}) => {
    const { selectedIds, data, resource: contextResource } = useListContext<VerificationRecord>();
    const notify = useNotify();
    const refresh = useRefresh();
    // Use the resource prop if provided, otherwise fall back to context resource
    const resourceName = resource || contextResource;
    const unselectAll = useUnselectAll(resourceName);

    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [mode, setMode] = useState<string | null>(null); // 'status' or 'verification'

    if (!display || selectedIds.length === 0) return null;

    const openDialog = (dialogMode: string): void => {
        setMode(dialogMode);
        setDialogOpen(true);
    };

    const handleDialogClose = (): void => {
        setDialogOpen(false);
    };

    /**
     * Core logic for updating verification status
     * Maps the selected rows and calls the appropriate API endpoint
     *
     * @param mode - The operation mode ('status' or 'verification')
     * @param statusValue - The numeric status value to set
     */
    const handleSave = (mode: string, statusValue: number): void => {
        handleDialogClose();

        let selectedRows = data.filter(row => selectedIds.includes(row.id));
        selectedRows = selectedRows.map(row => ({
            ...row,
            verification_status: (mode === 'verification') ? statusValue : row.verification_status,
            status: (mode === 'status') ? statusValue : row.status
        }));

        dataProvider.postData(resourceName, action || 'Update', selectedRows)
            .then(response => {
                if (response?.data && response.data?.success) {
                    notify(response.data.message, { type: 'success' });
                    refresh();
                } else {
                    notify(`Error: ${response?.body || response?.message || 'Unknown Error'}`, { type: 'error' });
                }
            })
            .catch((error: Error) => {
                notify(`Error: ${error.message}`, { type: 'error' });
            })
            .finally(() => {
                refresh();
                unselectAll();
                if (reload) reload();
            });
    };

    return (
        <>
            <Button sx={{
                backgroundColor: '#4caf50',
                color: 'white',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                    backgroundColor: '#388e3c'
                }
            }}
            variant='contained'
            startIcon={<CheckCircleOutline />}
            onClick={() => openDialog('status')}
            >
                Set Status
            </Button>

            <Button sx={{
                backgroundColor: '#f44336',
                color: 'white',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                    backgroundColor: '#d32f2f'
                }
            }}
            variant='contained'
            startIcon={<BlockOutlined />}
            onClick={() => openDialog('verification')}
            >
                Set Verification
            </Button>

            <SelectVerificationDialog
                open={dialogOpen}
                mode={mode || ''}
                selectedCount={selectedIds.length}
                onClose={handleDialogClose}
                onSave={handleSave}
                entityType={entityType}
            />
        </>
    );
};

export default VerificationActions;
